import { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FormService, FormConfig, FormState } from '@/services/FormService';
import { useToast } from '@/hooks/use-toast';

interface UseUniversalFormOptions<T> {
  formId: string;
  config: FormConfig;
  onSubmit: (data: T) => Promise<void>;
  initialData?: Partial<T>;
  autoSave?: boolean;
  persistState?: boolean;
}

export const useUniversalForm = <T extends Record<string, any>>({
  formId,
  config,
  onSubmit,
  initialData = {},
  autoSave = false,
  persistState = false,
}: UseUniversalFormOptions<T>) => {
  const { toast } = useToast();
  
  // Initialize form data
  const getInitialData = useCallback(() => {
    const defaults = FormService.getDefaultValues(config);
    const persisted = persistState ? FormService.loadFormState<T>(formId) : null;
    return { ...defaults, ...initialData, ...persisted } as T;
  }, [config, formId, initialData, persistState]);

  const [formState, setFormState] = useState<FormState<T>>(() => {
    const data = getInitialData();
    const validation = FormService.validateForm(data, config);
    
    return {
      data,
      errors: validation.errors,
      isValid: validation.isValid,
      isDirty: false,
      isSubmitting: false,
    };
  });

  // Submission mutation
  const submitMutation = useMutation({
    mutationFn: async (data: T) => {
      const formatted = FormService.formatFormData(data, config);
      await onSubmit(formatted);
    },
    onSuccess: () => {
      if (config.resetOnSubmit) {
        resetForm();
      }
      if (persistState) {
        FormService.clearFormState(formId);
      }
      setFormState(prev => ({ ...prev, isDirty: false }));
      toast({
        title: 'Success',
        description: 'Form submitted successfully',
      });
    },
    onError: (error) => {
      console.error('Form submission error:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit form. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update field value
  const updateField = useCallback((name: string, value: any) => {
    setFormState(prev => {
      const newData = { ...prev.data, [name]: value };
      const validation = FormService.validateForm(newData, config);
      
      const newState = {
        data: newData,
        errors: validation.errors,
        isValid: validation.isValid,
        isDirty: true,
        isSubmitting: prev.isSubmitting,
      };

      // Auto-save to localStorage if enabled
      if (persistState && validation.isValid) {
        FormService.saveFormState(formId, newData);
      }

      return newState;
    });
  }, [config, formId, persistState]);

  // Update multiple fields
  const updateFields = useCallback((updates: Partial<T>) => {
    setFormState(prev => {
      const newData = { ...prev.data, ...updates };
      const validation = FormService.validateForm(newData, config);
      
      const newState = {
        data: newData,
        errors: validation.errors,
        isValid: validation.isValid,
        isDirty: true,
        isSubmitting: prev.isSubmitting,
      };

      if (persistState && validation.isValid) {
        FormService.saveFormState(formId, newData);
      }

      return newState;
    });
  }, [config, formId, persistState]);

  // Submit form
  const submitForm = useCallback(() => {
    if (!formState.isValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before submitting',
        variant: 'destructive',
      });
      return;
    }

    setFormState(prev => ({ ...prev, isSubmitting: true }));
    submitMutation.mutate(formState.data);
  }, [formState.isValid, formState.data, submitMutation, toast]);

  // Reset form
  const resetForm = useCallback(() => {
    const data = getInitialData();
    const validation = FormService.validateForm(data, config);
    
    setFormState({
      data,
      errors: validation.errors,
      isValid: validation.isValid,
      isDirty: false,
      isSubmitting: false,
    });

    if (persistState) {
      FormService.clearFormState(formId);
    }
  }, [getInitialData, config, formId, persistState]);

  // Auto-save effect
  useEffect(() => {
    if (autoSave && formState.isDirty && formState.isValid) {
      const timeoutId = setTimeout(() => {
        submitForm();
      }, 2000); // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(timeoutId);
    }
  }, [autoSave, formState.isDirty, formState.isValid, submitForm]);

  // Update isSubmitting when mutation state changes
  useEffect(() => {
    setFormState(prev => ({ ...prev, isSubmitting: submitMutation.isPending }));
  }, [submitMutation.isPending]);

  return {
    // Form state
    data: formState.data,
    errors: formState.errors,
    isValid: formState.isValid,
    isDirty: formState.isDirty,
    isSubmitting: formState.isSubmitting,
    
    // Actions
    updateField,
    updateFields,
    submitForm,
    resetForm,
    
    // Config
    config,
  };
};